import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Avatar,
  styled,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import Help from "../assets/images/help-desk.svg";
import Notifications from "../assets/images/notifications-icon.svg";
import AppServices from "../assets/images/app-services.svg";
import SecurityIcon from "../assets/images/settings/security.svg";
import ProfileIcon from "../assets/images/user_profile.svg";
import AccessControlIcon from "../assets/images/settings/access_control.svg";
import LogoutIcon from "../assets/images/logout.svg";
import LockIcon from "../assets/images/lock.svg";
import NotificationPopover from "./notification_popover";
import PathComponent from "./path_component";
import { LockScreen } from "./sidebar/lock_screen";
import { eNums, SETTINGS_KEYS, ROUTES, COOKIE_KEYS } from "../constants";
import { handleLogout, useRolePermissions, useUserInfo } from "../utilities";
import cookieStorage from "../utilities/cookie_storage";

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 4,
    marginTop: theme.spacing(1.5),
    minWidth: 260,
    border: '.5px solid var(--light-gray)',
    '& .MuiMenu-list': {
      padding: '0',
    },
  },
}));

const SearchAndIcons = ({ userType, props }) => {
  const navigate = useNavigate();
  const { userName, userEmail, roleLevel, lockEnabled } = useUserInfo();

  const Permissions = useRolePermissions();

  const rbacPermissions = Permissions?.settings?.features?.role_based_access_control;
  const view_role_management_permission = Permissions?.settings?.enabled && rbacPermissions?.enabled &&
      rbacPermissions?.subFeatures?.view_role_management?.enabled;
  const have_access_update_to_role_management_permission = Permissions?.settings?.enabled && rbacPermissions?.enabled &&
      rbacPermissions?.subFeatures?.have_access_update_to_role_management?.enabled;

  const [anchorProfile, setAnchorProfile] = useState(null);
  const openProfile = Boolean(anchorProfile);

  const [anchorNoti, setAnchorNoti] = useState(null);
  const openNoti = Boolean(anchorNoti);

  const [isLocked, setIsLocked] = useState(!!cookieStorage.getItem(COOKIE_KEYS.APP_LOCKED));
  const [showWorkspace, setShowWorkspace] = useState(false);

  const handleClose = () => {
    setAnchorProfile(null);
    setAnchorNoti(null);
    setTimeout(() => setShowWorkspace(false), 200);
  };

  const handleClickProfile = (e) => {
    setAnchorProfile(e?.currentTarget);
  };

  const handleClickNotification = (e) => {
    setAnchorNoti(e?.currentTarget);
  };

  const handleLock = () => {
    cookieStorage.setItem(COOKIE_KEYS.APP_LOCKED, 'true');
    setIsLocked(true);
    setAnchorProfile(null);
  };

  const handleUnlock = () => {
    cookieStorage.removeItem(COOKIE_KEYS.APP_LOCKED);
    setIsLocked(false);
  };

  if (isLocked) {
    return <LockScreen onUnlock={handleUnlock} />;
  }

  const profileMenuItems = [
    { label: "My Profile", icon: ProfileIcon, parentKey: SETTINGS_KEYS.COMPANY_PROFILE, sectionKey: SETTINGS_KEYS.PRIMARY_DETAILS },
    { label: "Account Security", icon: SecurityIcon, parentKey: SETTINGS_KEYS.ACCOUNT_SECURITY, sectionKey: SETTINGS_KEYS.LOGIN },
    {
      label: "Access Control", icon: AccessControlIcon,
      parentKey: SETTINGS_KEYS.ACCESS_CONTROL, sectionKey: SETTINGS_KEYS.ROLE_MANAGEMENT,
      show: userType === eNums?.AGENT && ((roleLevel === 0 || roleLevel === 1) || (view_role_management_permission || have_access_update_to_role_management_permission)),
    },
    { label: "Notification Preferences", icon: Notifications, parentKey: SETTINGS_KEYS.NOTIFICATIONS, sectionKey: SETTINGS_KEYS.NOTIFICATION_PREFERENCES },
  ];

  return (
    <div className="Settings-search">
      <div className="wrap-settings-page py-0 flex items-center justify-between">
        <PathComponent paths={props?.paths} />
      </div>
      <div className="flex">
        <IconButton onClick={handleClickNotification} className="h-full my-auto">
          <img src={Notifications} alt="" width={16} height={16} />
        </IconButton>
        <IconButton className="h-full my-auto">
          <img src={Help} alt="" width={20} height={20} />
        </IconButton>

        <NotificationPopover
          anchorEl={anchorNoti}
          open={openNoti}
          onClose={handleClose}
        />

        <IconButton className="h-full my-auto">
          <img src={AppServices} alt="" width={16} height={16} />
        </IconButton>
        <IconButton
          id="profile-button"
          aria-controls={openProfile ? "profile-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openProfile ? "true" : undefined}
          onClick={handleClickProfile}
          className="h-full my-auto"
        >
          {userName ? (
            <div className="user-profile-icon flex items-center justify-center cursor-pointer">
              {userName?.[0]}
            </div>
          ) : (
            <AccountCircleIcon />
          )}
        </IconButton>

        <StyledMenu
          id="profile-menu"
          anchorEl={anchorProfile}
          open={openProfile}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "profile-button" }}
        >
          {!showWorkspace ? (
            <>
              <Box className="flex items-center p-3 gap-3">
                <Box position="relative">
                  <Avatar sx={{ bgcolor: '#0066FF', width: 32, height: 32, fontSize: '12px', fontWeight: 600 }}>
                    {userName?.[0]?.toUpperCase() || 'D'}
                  </Avatar>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#22C55E',
                    border: '2px solid white',
                    position: 'absolute',
                    bottom: 2,
                    right: 0,
                  }} />
                </Box>
                <Box>
                  <Typography fontWeight={600} fontSize={14} className="text-charcoal">
                    {userName || "You"}
                  </Typography>
                  <Typography fontWeight={400} fontSize={10} className="text-dim-gray">
                    {userEmail}
                  </Typography>
                </Box>
              </Box>

              <div className="border-bottom-gray-5 mb-1" />

              <Box className="pb-1">
                {profileMenuItems.map((item, index) =>
                  item.show !== false && (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        navigate(ROUTES.SETTINGS, { state: { parentKey: item.parentKey, sectionKey: item.sectionKey } });
                        handleClose();
                      }}
                      className="mx-2 px-6 flex items-center justify-between rounded hover-bg-f8 hover-fw-500"
                      sx={{ minHeight: 'auto', py: '8px' }}
                    >
                      <Box className="flex items-center gap-2">
                        <img src={item.icon} alt="" width={16} height={16} />
                        <Typography className="text-12 text-charcoal item-text" fontWeight={400}>{item.label}</Typography>
                      </Box>
                      <ChevronRightIcon sx={{ fontSize: 18 }} className="text-charcoal" />
                    </MenuItem>
                  )
                )}
              </Box>

              <div className="border-bottom-gray-5 mb-1" />

              <Box className="pb-1">
                {lockEnabled && (
                  <MenuItem onClick={handleLock} className="mx-2 my-1 px-6 flex items-center rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                    <Box className="flex items-center gap-2">
                      <img src={LockIcon} alt="" width={16} height={16} />
                      <Typography className="text-10 text-charcoal item-text" fontWeight={400}>Lock</Typography>
                    </Box>
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout} className="mx-2 my-1 px-6 flex items-center rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                  <Box className="flex items-center gap-2">
                    <img src={LogoutIcon} alt="" width={16} height={16} />
                    <Typography className="text-10 text-alert-red item-text" fontWeight={400}>Sign Out</Typography>
                  </Box>
                </MenuItem>
              </Box>
            </>
          ) : (
            <>
              <Box className="py-1">
                <MenuItem className="mx-2 my-1 px-6 flex items-center justify-between rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                  <Box className="flex items-center gap-2">
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'transparent', color: '#374151', border: '1px solid #D1D5DB' }}>
                      <PersonOutlineIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Typography className="text-10 text-charcoal item-text" fontWeight={400}>Workspace 1</Typography>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18 }} className="text-charcoal" />
                </MenuItem>

                <MenuItem className="mx-2 my-1 px-6 flex items-center justify-between rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                  <Box className="flex items-center gap-2">
                    <WorkOutlineIcon sx={{ fontSize: 20, color: '#374151' }} />
                    <Typography className="text-10 text-charcoal item-text" fontWeight={400}>Workspace 2</Typography>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18 }} className="text-charcoal" />
                </MenuItem>

                <MenuItem className="mx-2 my-1 px-6 flex items-center justify-between rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                  <Box className="flex items-center gap-2">
                    <WorkOutlineIcon sx={{ fontSize: 20, color: '#374151' }} />
                    <Typography className="text-10 text-charcoal item-text" fontWeight={400}>Workspace 3</Typography>
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18 }} className="text-charcoal" />
                </MenuItem>
              </Box>

              <div className="border-bottom-gray-5 mb-1" />

              <Box className="pb-1">
                <MenuItem onClick={() => setShowWorkspace(false)} className="mx-2 my-1 px-6 flex items-center justify-between rounded hover-bg-f8 hover-fw-500" sx={{ minHeight: 'auto', py: '10px' }}>
                  <Box className="flex items-center">
                    <Typography className="text-10 text-charcoal item-text" fontWeight={400}>Create New Workspace</Typography>
                  </Box>
                  <AddIcon sx={{ color: '#374151', fontSize: 20 }} />
                </MenuItem>
              </Box>
            </>
          )}
        </StyledMenu>
      </div>
    </div>
  );
};

export default SearchAndIcons;
